import { useEffect, useState } from 'react';
import type { RecommendationDoc, ReportReason } from '@astrally/shared';
import { useApp, type ChatEntry } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { GradeBadge, initialsOf } from '../../components/common';

/**
 * Chat rendering shared by the Ally screen and Home. Both call `useApp()`, so
 * they may only be mounted inside the provider — which every screen already is.
 */

export type RecAction = 'why' | 'request' | 'dismiss' | 'not-similar';

export interface BubbleProps {
  entry: ChatEntry;
  onAction: (rec: RecommendationDoc, a: RecAction) => Promise<void>;
  /** Timestamp of the entry whose sources are expanded, or null. */
  openSources: string | null;
  setOpenSources: (id: string | null) => void;
  /** Resolves a cited fact id against the grounding pack. */
  factLookup: (id: string) => { label: string; value: string } | undefined;
}

export function Bubble({ entry, onAction, openSources, setOpenSources, factLookup }: BubbleProps) {
  const { t } = useI18n();
  const app = useApp();
  const [acting, setActing] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const sourcesId = entry.ts;

  const act = async (rec: RecommendationDoc, a: RecAction) => {
    setActing(true);
    try {
      await onAction(rec, a);
    } catch (e) {
      alert(String(e));
    } finally {
      setActing(false);
    }
  };

  const report = async (rec: RecommendationDoc, reason: ReportReason) => {
    await app.backend.reportUser(rec.candidateUid, reason, `recommendation:${rec.id}`);
    setReportOpen(false);
    alert('Thank you. Our safety team will review this.');
  };

  return (
    /*
      A message is a ROW: the row picks the side, the bubble keeps its shape,
      and Ally's avatar dot sits in the gutter outside the bubble. `.bubble--user`
      and `.bubble--ally` stay exactly where they were, on the bubble itself —
      they are the class pair everything else in the app (and the tests) reads
      to tell the two speakers apart.
    */
    <div className={`bubble-row bubble-row--${entry.role} fade-in`}>
      {entry.role === 'ally' && <span className="bubble-avatar" aria-hidden />}
      <div className={`bubble bubble--${entry.role}`}>
        {entry.role === 'ally' && entry.creditCost ? (
          <div className="tiny gold" style={{ marginBottom: 6 }}>
            {entry.modelClass === 'master' ? '◆ Master Reading' : '◇ Go Deeper'} · {entry.creditCost} credits
          </div>
        ) : null}
        {entry.text}

        {entry.recommendation && (
          <RecCard
            rec={entry.recommendation}
            acting={acting}
            onAct={(a) => act(entry.recommendation!, a)}
            reportOpen={reportOpen}
            setReportOpen={setReportOpen}
            onReport={(reason) => report(entry.recommendation!, reason)}
          />
        )}

        {entry.role === 'ally' && (entry.citedFactIds?.length ?? 0) > 0 && (
          <div style={{ marginTop: 10 }}>
            <button
              className="chip chip--fact"
              onClick={() => setOpenSources(openSources === sourcesId ? null : sourcesId)}
            >
              ☉ {t('ally.sources')} ({entry.citedFactIds!.length})
            </button>
            {openSources === sourcesId && (
              <div className="col" style={{ gap: 6, marginTop: 8 }}>
                {entry.citedFactIds!.map((id) => {
                  const fact = factLookup(id);
                  return (
                    <div key={id} className="tiny gold">
                      <span style={{ opacity: 0.7 }}>{fact?.label ?? id}:</span>{' '}
                      {fact?.value ?? 'calculated value'}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export interface RecCardProps {
  rec: RecommendationDoc;
  /** True while an action is in flight — every control locks together. */
  acting: boolean;
  onAct: (a: RecAction) => void;
  reportOpen: boolean;
  setReportOpen: (b: boolean) => void;
  onReport: (reason: ReportReason) => void;
}

export function RecCard({ rec, acting, onAct, reportOpen, setReportOpen, onReport }: RecCardProps) {
  const { t } = useI18n();
  const app = useApp();
  const [name, setName] = useState(rec.candidateUid.replace('demo-', ''));
  const [bio, setBio] = useState('');

  useEffect(() => {
    app.backend
      .getUser(rec.candidateUid)
      .then((u) => {
        if (u) {
          setName(u.displayName);
          setBio(u.bio ?? '');
        }
      })
      // A profile that cannot be read is not an error worth surfacing — the
      // card already renders with the fallback name set above. getUser maps
      // permission-denied to null, but offline and transient failures still
      // reject, and an uncaught one here is an unhandled rejection fired from
      // inside an effect on a screen the reader is looking at.
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.candidateUid]);

  const requested = rec.status === 'requested';

  return (
    <div className="card card--strong" style={{ marginTop: 12 }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <div className="avatar avatar--gold">{initialsOf(name)}</div>
        <div style={{ flex: 1 }}>
          <div className="h3" style={{ textTransform: 'capitalize' }}>{name}</div>
          {bio && <div className="tiny muted">{bio}</div>}
        </div>
        <GradeBadge grade={rec.grades.overall} />
      </div>

      <div className="wrap" style={{ marginBottom: 8 }}>
        {rec.grades.communicationFit && (
          <span className="tiny muted row">
            communication <GradeBadge grade={rec.grades.communicationFit} />
          </span>
        )}
        {rec.grades.sharedInterests && (
          <span className="tiny muted row">
            interests <GradeBadge grade={rec.grades.sharedInterests} />
          </span>
        )}
        {rec.grades.jyotishCompat && (
          <span className="tiny muted row">
            jyotish <GradeBadge grade={rec.grades.jyotishCompat} />
          </span>
        )}
        {rec.grades.westernCompat && (
          <span className="tiny muted row">
            western <GradeBadge grade={rec.grades.westernCompat} />
          </span>
        )}
      </div>

      {rec.sharedInterests.length > 0 && (
        <div className="tiny muted" style={{ marginBottom: 10 }}>
          Shared: {rec.sharedInterests.join(', ')}
        </div>
      )}

      {requested ? (
        <div className="banner banner--info small">{t('ally.rec.requested')}</div>
      ) : (
        <div className="wrap">
          <button className="chip" disabled={acting} onClick={() => onAct('why')}>
            {t('ally.rec.why')}
          </button>
          <button className="chip chip--on" disabled={acting} onClick={() => onAct('request')}>
            {t('ally.rec.request')}
          </button>
          <button className="chip" disabled={acting} onClick={() => onAct('dismiss')}>
            {t('ally.rec.skip')}
          </button>
          <button className="chip" disabled={acting} onClick={() => onAct('not-similar')}>
            {t('ally.rec.notSimilar')}
          </button>
          <button className="chip danger" onClick={() => setReportOpen(!reportOpen)}>
            {t('ally.rec.report')}
          </button>
        </div>
      )}

      {reportOpen && (
        <div className="wrap" style={{ marginTop: 8 }}>
          {(['fake-profile', 'inappropriate-content', 'safety-concern', 'other'] as ReportReason[]).map(
            (r) => (
              <button key={r} className="chip" onClick={() => onReport(r)}>
                {r.replace(/-/g, ' ')}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}
